'use client';

import React, { useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import Button1 from '@/components/Button1';
import AddToCart from '@/components/AddToCart';
import LazySection from '@/components/LazySection';
import { COLORS } from '@/constants/colors';
import { FONT_FAMILY, TYPOGRAPHY } from '@/constants/typography';
import { SPACING } from '@/constants/spacing';
import { media } from '@/constants/breakpoints';
import { formatPriceWithCurrency } from '@/utils/formatters';
import { formatRange, type VehicleLandingData, type FitmentRow, type PartSummary } from './vehicleLandingData';

const ALL = 'All';
const DISPLAY_FONT = `var(--font-display), ${FONT_FAMILY.primary}`;

export interface Vehicle {
  /** Make as customers know it, e.g. "Chevrolet" (the CMS may store it as "FORD"). */
  make: string;
  /** Base model name, e.g. "Corvette"; variants like "Corvette Z06" are grouped under it. */
  model: string;
  /** Used for element ids, e.g. "corvette". */
  slug: string;
}

interface VehicleLandingPageProps {
  vehicle: Vehicle;
  data: VehicleLandingData;
}

// "for the Corvette, Corvette Z06 and Corvette ZR1" / "for the Mustang, Mustang GT, Mustang Bullitt and more"
function modelList(models: string[]): string {
  if (models.length <= 3) return models.length > 1 ? `${models.slice(0, -1).join(', ')} and ${models[models.length - 1]}` : models[0] ?? '';
  return `${models.slice(0, 3).join(', ')} and more`;
}

const VehicleLandingPage = ({ vehicle, data }: VehicleLandingPageProps) => {
  const name = `${vehicle.make} ${vehicle.model}`;
  const filters = data.parts.filter((p) => p.kind === 'filter');
  const kits = data.parts.filter((p) => p.kind === 'kit');
  const heroFilter = filters[0];

  return (
    <>
      <Header />
      <div className="header-gap-creator"></div>
      <Breadcrumbs items={[{ label: 'Shop by Vehicle', href: '/shop-by-vehicle' }, { label: name }]} />

      <main>
        <Hero>
          <HeroInner>
            <HeroCopy>
              <Eyebrow>
                {name} · {data.yearMin}–{data.yearMax}
              </Eyebrow>
              <HeroTitle>
                High performance air filters for <Accent>every {vehicle.model}</Accent>
              </HeroTitle>
              <HeroText>
                High-flow Green Filter performance air filters for the {modelList(data.models)}, built to help your engine
                breathe.
                Washable, reusable and made in the USA. Pick your year to see the exact filter for your engine.
              </HeroText>
              <Stats>
                <Stat>
                  <StatValue>{filters.length}</StatValue>
                  <StatLabel>filters</StatLabel>
                </Stat>
                <Stat>
                  <StatValue>{data.rows.length}</StatValue>
                  <StatLabel>fitments</StatLabel>
                </Stat>
                <Stat>
                  <StatValue>{data.yearMax - data.yearMin + 1}</StatValue>
                  <StatLabel>model years</StatLabel>
                </Stat>
              </Stats>
            </HeroCopy>

            <Finder vehicle={vehicle} data={data} />
          </HeroInner>
          {heroFilter?.imageUrl && (
            <HeroImage aria-hidden="true">
              <Image src={heroFilter.imageUrl} alt="" width={360} height={360} priority />
            </HeroImage>
          )}
        </Hero>

        <Section id="filters">
          <SectionHead>
            <SectionTitle>High performance filters for the {vehicle.model}</SectionTitle>
            <SectionText>Every high-flow Green Filter listed for the {vehicle.model}, newest models first.</SectionText>
          </SectionHead>
          <CardGrid>
            {filters.map((part) => (
              <PartCard key={part.partNum} part={part} />
            ))}
          </CardGrid>
        </Section>

        {data.care.length > 0 && (
          <Section id="care" $muted>
            <SectionHead>
              <SectionTitle>Keep your filter clean</SectionTitle>
              <SectionText>
                Green Filters are washable and reusable. Clean and re-oil yours with these to keep it flowing like new.
              </SectionText>
            </SectionHead>
            <CardGrid>
              {data.care.map((part) => (
                <PartCard key={part.partNum} part={part} />
              ))}
            </CardGrid>
          </Section>
        )}

        <Section id="fitment-chart">
          <SectionHead>
            <SectionTitle>Complete {vehicle.model} fitment chart</SectionTitle>
            <SectionText>Every year and engine we have a filter for, exactly as listed in our catalog.</SectionText>
          </SectionHead>
          <FitmentChart rows={data.rows} models={data.models} parts={data.parts} />
        </Section>

        {kits.length > 0 && (
          <Section id="upgrade" $muted>
            <SectionHead>
              <SectionTitle>Upgrade with a Pedal Power Kit</SectionTitle>
              <SectionText>A Green Filter air filter bundled with a throttle controller for sharper response.</SectionText>
            </SectionHead>
            <CardGrid>
              {kits.map((part) => (
                <PartCard key={part.partNum} part={part} />
              ))}
            </CardGrid>
          </Section>
        )}

        <Section>
          <Benefits>
            <Benefit>
              <BenefitTitle>High-flow performance</BenefitTitle>
              <BenefitText>Designed for high airflow, so your {vehicle.model}&apos;s engine can breathe freely.</BenefitText>
            </Benefit>
            <Benefit>
              <BenefitTitle>Made in the USA</BenefitTitle>
              <BenefitText>Built and inspected in the USA, from the filter media to the final check.</BenefitText>
            </Benefit>
            <Benefit>
              <BenefitTitle>Washable &amp; reusable</BenefitTitle>
              <BenefitText>Clean it, re-oil it and reuse it instead of buying a new paper filter.</BenefitText>
            </Benefit>
            <Benefit>
              <BenefitTitle>Lifetime warranty</BenefitTitle>
              <BenefitText>
                Backed by our <Link href="/our-guarantee">lifetime warranty</Link>.
              </BenefitText>
            </Benefit>
          </Benefits>
        </Section>

        <HelpBand>
          <HelpTitle>Don&apos;t see your {vehicle.model}?</HelpTitle>
          <HelpText>Tell us the year and engine and we&apos;ll help you find the right filter.</HelpText>
          <Button1 isLink to="/contact-us" text="Contact us" />
        </HelpBand>
      </main>

      <Footer />
    </>
  );
};

export default VehicleLandingPage;

/* ---------- Year finder ---------- */

const Finder = ({ vehicle, data }: { vehicle: Vehicle; data: VehicleLandingData }) => {
  const yearId = `${vehicle.slug}-year`;
  const modelId = `${vehicle.slug}-model`;
  const shortName = (m: string) => m.replace(new RegExp(`^${vehicle.model}\\s*`), '') || vehicle.model;
  const [year, setYear] = useState<number | null>(null);
  const [model, setModel] = useState<string>(ALL);

  const years = useMemo(() => {
    const covered = new Set<number>();
    data.rows.forEach((r) => {
      for (let y = r.yearStart; y <= r.yearEnd; y++) covered.add(y);
    });
    return [...covered].sort((a, b) => b - a);
  }, [data.rows]);

  const matches = useMemo(() => {
    if (year === null) return [];
    const rows = data.rows.filter((r) => r.yearStart <= year && r.yearEnd >= year && (model === ALL || r.model === model));
    const byPart = new Map<string, FitmentRow[]>();
    rows.forEach((r) => byPart.set(r.partNum, [...(byPart.get(r.partNum) ?? []), r]));
    return [...byPart.entries()]
      .map(([partNum, fits]) => ({ part: data.parts.find((p) => p.partNum === partNum)!, fits }))
      .sort((a, b) => (a.part.kind === b.part.kind ? a.part.partNum.localeCompare(b.part.partNum) : a.part.kind === 'filter' ? -1 : 1));
  }, [data.rows, data.parts, year, model]);

  return (
    <FinderCard aria-labelledby="finder-title">
      <FinderTitle id="finder-title">Find your filter</FinderTitle>
      <FieldLabel htmlFor={yearId}>Model year</FieldLabel>
      <Select id={yearId} value={year ?? ''} onChange={(e) => setYear(e.target.value ? Number(e.target.value) : null)}>
        <option value="">Select year</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </Select>

      {/* A few models fit as buttons; more (e.g. Mustang's GT, Bullitt, Mach 1, ...) use a dropdown. */}
      {data.models.length > 1 && data.models.length <= 4 && (
        <>
          <FieldLabel as="span">Model</FieldLabel>
          <Segmented role="group" aria-label={`${vehicle.model} model`}>
            {[ALL, ...data.models].map((m) => (
              <SegmentButton key={m} type="button" $active={model === m} aria-pressed={model === m} onClick={() => setModel(m)}>
                {m === ALL ? 'All' : shortName(m)}
              </SegmentButton>
            ))}
          </Segmented>
        </>
      )}
      {data.models.length > 4 && (
        <>
          <FieldLabel htmlFor={modelId}>Model</FieldLabel>
          <Select id={modelId} value={model} onChange={(e) => setModel(e.target.value)}>
            <option value={ALL}>All {vehicle.model} models</option>
            {data.models.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </>
      )}

      <Results aria-live="polite">
        {year === null && <ResultHint>Choose a year to see the matching filter.</ResultHint>}
        {year !== null && matches.length === 0 && (
          <ResultHint>
            We don&apos;t list a filter for a {year} {model === ALL ? vehicle.model : model} yet.{' '}
            <Link href="/contact-us">Contact us</Link>
            {' '}and we&apos;ll help.
          </ResultHint>
        )}
        {matches.map(({ part, fits }) => (
          <ResultItem key={part.partNum}>
            {part.imageUrl && (
              <ResultThumb>
                <Image src={part.imageUrl} alt={part.name} width={64} height={64} />
              </ResultThumb>
            )}
            <ResultBody>
              <ResultPart>
                {part.kind === 'kit' ? 'Pedal Power Kit' : 'Green Filter'} #{part.partNum}
              </ResultPart>
              <ResultMeta>
                {[[...new Set(fits.map((f) => f.model))].join(', '), uniqueEngines(fits.map((f) => f.engine).filter(Boolean)).join(', ')]
                  .filter(Boolean)
                  .join(' · ')}
              </ResultMeta>
            </ResultBody>
            <ResultLink href={`#part-${part.partNum}`}>View</ResultLink>
          </ResultItem>
        ))}
        {matches.some(({ part }) => data.care.some((c) => c.worksWith.includes(part.partNum))) && (
          <CareHint href="#care">+ Keep it clean: cleaner &amp; recharge oil</CareHint>
        )}
      </Results>
    </FinderCard>
  );
};

/* ---------- Product card ---------- */

// "350 V8" and "350 CID V8" are the same engine written two ways in the catalog;
// the card lists each engine once (the full chart below still shows every row as stored).
function uniqueEngines(engines: string[]): string[] {
  const seen = new Map<string, string>();
  engines.forEach((e) => {
    const key = e.replace(/\s+CID\b/i, '').toLowerCase();
    if (!seen.has(key)) seen.set(key, e.replace(/\s+CID\b/i, ''));
  });
  return [...seen.values()];
}

const PartCard = ({ part }: { part: PartSummary }) => {
  const [cartUnavailable, setCartUnavailable] = useState(false);
  const canBuy = Boolean(part.buyButtonId) && !cartUnavailable;

  return (
    <Card id={`part-${part.partNum}`}>
      <CardImage href={part.href} aria-label={`View ${part.name}`}>
        {part.imageUrl ? (
          <Image src={part.imageUrl} alt={part.name} fill sizes="(max-width: 640px) 90vw, 300px" />
        ) : (
          <NoImage>#{part.partNum}</NoImage>
        )}
      </CardImage>
      <CardBody>
        <CardKicker>{part.kind === 'kit' ? 'Pedal Power Kit' : part.kind === 'care' ? 'Filter care' : 'High performance filter'}</CardKicker>
        <CardTitle>
          <Link href={part.href}>#{part.partNum}</Link>
        </CardTitle>
        {part.kind === 'care' && <CardSubtitle>{part.name}</CardSubtitle>}
        {part.kind === 'care' ? (
          <>
            <FitHeading>Works with</FitHeading>
            <Chips>
              {part.worksWith.map((partNum) => (
                <Chip key={partNum} href={`#part-${partNum}`}>
                  #{partNum}
                </Chip>
              ))}
            </Chips>
          </>
        ) : (
          <FitHeading>Fits</FitHeading>
        )}
        <FitList>
          {part.fits.map((fit) => (
            <FitItem key={fit.model}>
              <FitRow>
                <FitModel>{fit.model}</FitModel>
                <FitYears>{fit.ranges.map(formatRange).join(', ')}</FitYears>
              </FitRow>
              {fit.engines.length > 0 && <FitEngines>{uniqueEngines(fit.engines).join(', ')}</FitEngines>}
            </FitItem>
          ))}
        </FitList>
      </CardBody>
      <CardFooter>
        {canBuy ? (
          <LazySection rootMargin="400px">
            <AddToCart
              buyButtonId={part.buyButtonId as string}
              id={part.partNum}
              showOptions={part.kind === 'kit'}
              hidePrice={false}
              isLeft
              priceSize="24px"
              compareAtSize="14px"
              onError={() => setCartUnavailable(true)}
            />
          </LazySection>
        ) : (
          part.price !== null && <Price>{formatPriceWithCurrency(Math.round(part.price * 100))}</Price>
        )}
        <DetailsButton href={part.href}>View details</DetailsButton>
      </CardFooter>
    </Card>
  );
};

/* ---------- Fitment chart ---------- */

const FitmentChart = ({ rows, models, parts }: { rows: FitmentRow[]; models: string[]; parts: PartSummary[] }) => {
  const [model, setModel] = useState<string>(ALL);
  const shown = model === ALL ? rows : rows.filter((r) => r.model === model);
  const kindOf = (partNum: string) => parts.find((p) => p.partNum === partNum)?.kind ?? 'filter';

  return (
    <>
      <Tabs role="tablist" aria-label="Filter the chart by model">
        {[ALL, ...models].map((m) => (
          <Tab key={m} role="tab" type="button" aria-selected={model === m} $active={model === m} onClick={() => setModel(m)}>
            {m === ALL ? 'All models' : m}
            <TabCount>{m === ALL ? rows.length : rows.filter((r) => r.model === m).length}</TabCount>
          </Tab>
        ))}
      </Tabs>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <th scope="col">Years</th>
              <th scope="col">Model</th>
              <th scope="col">Engine</th>
              <th scope="col">Fuel system</th>
              <th scope="col">Part</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id}>
                <td>
                  <strong>{formatRange({ start: r.yearStart, end: r.yearEnd })}</strong>
                </td>
                <td>{[r.model, r.trim].filter(Boolean).join(' ')}</td>
                <td>{r.engine || '—'}</td>
                <td>{r.fuelSystem || '—'}</td>
                <td>
                  <PartLink href={`#part-${r.partNum}`} $kit={kindOf(r.partNum) === 'kit'}>
                    {kindOf(r.partNum) === 'kit' ? 'Kit ' : ''}#{r.partNum}
                  </PartLink>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {/* Phones: the same rows as stacked cards instead of a sideways-scrolling table. */}
      <RowCards>
        {shown.map((r) => (
          <RowCard key={r.id}>
            <RowCardTop>
              <strong>{formatRange({ start: r.yearStart, end: r.yearEnd })}</strong>
              <PartLink href={`#part-${r.partNum}`} $kit={kindOf(r.partNum) === 'kit'}>
                {kindOf(r.partNum) === 'kit' ? 'Kit ' : ''}#{r.partNum}
              </PartLink>
            </RowCardTop>
            <RowCardMeta>{[[r.model, r.trim].filter(Boolean).join(' '), r.engine, r.fuelSystem].filter(Boolean).join(' · ')}</RowCardMeta>
          </RowCard>
        ))}
      </RowCards>
    </>
  );
};

/* ---------- Styles ---------- */

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 85% 30%, ${COLORS.brandGreen}33 0%, transparent 45%),
    linear-gradient(160deg, ${COLORS.gray999} 0%, ${COLORS.gray900} 100%);
  color: ${COLORS.white};
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${SPACING.sectionSmall} ${SPACING.lg} ${SPACING.sectionLarge};
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: ${SPACING.xxl};
  align-items: start;

  ${media.desktop} {
    padding-top: ${SPACING.sectionLarge};
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: ${SPACING.xl};
    padding-bottom: ${SPACING.sectionSmall};
  }

  ${media.mobile} {
    padding: ${SPACING.xl} ${SPACING.md} ${SPACING.xxl};
  }
`;

const HeroCopy = styled.div`
  padding-top: ${SPACING.md};
`;

const HeroImage = styled.div`
  position: absolute;
  right: -40px;
  bottom: -60px;
  opacity: 0.18;
  pointer-events: none;

  img {
    border-radius: 50%;
    filter: grayscale(0.2);
  }

  @media (max-width: 960px) {
    display: none;
  }
`;

const Eyebrow = styled.p`
  ${TYPOGRAPHY.label}
  color: ${COLORS.brandGreen};
  text-transform: uppercase;
  letter-spacing: 0.14em;
  margin: 0 0 ${SPACING.sm};
`;

const HeroTitle = styled.h1`
  font-family: ${DISPLAY_FONT};
  font-weight: 800;
  font-size: clamp(2.6rem, 6vw, 4.4rem);
  line-height: 0.98;
  text-transform: uppercase;
  letter-spacing: 0.01em;
  color: ${COLORS.white};
  margin: 0 0 ${SPACING.md};
`;

const Accent = styled.span`
  color: ${COLORS.brandGreen};
`;

const HeroText = styled.p`
  ${TYPOGRAPHY.bodyLG}
  color: ${COLORS.gray400};
  max-width: 34rem;
  margin: 0 0 ${SPACING.xl};
`;

const Stats = styled.div`
  display: flex;
  gap: ${SPACING.xl};

  ${media.mobile} {
    gap: ${SPACING.lg};
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: ${SPACING.md};
  border-left: 2px solid ${COLORS.brandGreen};
`;

const StatValue = styled.span`
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 2.2rem;
  line-height: 1;
`;

const StatLabel = styled.span`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textMuted};
`;

const FinderCard = styled.section`
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  border-radius: 16px;
  padding: ${SPACING.lg};
  box-shadow: 0 24px 60px ${COLORS.brandBlack}59;

  ${media.mobile} {
    padding: ${SPACING.md};
    border-radius: 12px;
  }
`;

const FinderTitle = styled.h2`
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 1.75rem;
  text-transform: uppercase;
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.md};
`;

const FieldLabel = styled.label`
  ${TYPOGRAPHY.label}
  display: block;
  color: ${COLORS.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
  margin: 0 0 6px;
`;

const Select = styled.select`
  ${TYPOGRAPHY.body}
  width: 100%;
  height: 52px;
  padding: 0 ${SPACING.md};
  border: 2px solid ${COLORS.borderGray};
  border-radius: 10px;
  background: ${COLORS.white};
  color: ${COLORS.textPrimary};
  margin-bottom: ${SPACING.md};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${COLORS.brandGreen};
    box-shadow: 0 0 0 3px ${COLORS.brandGreen}33;
  }
`;

const Segmented = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 4px;
  padding: 4px;
  background: ${COLORS.gray300};
  border-radius: 10px;
  margin-bottom: ${SPACING.md};
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  ${TYPOGRAPHY.buttonSM}
  min-height: 40px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) => (p.$active ? COLORS.white : 'transparent')};
  color: ${(p) => (p.$active ? COLORS.textPrimary : COLORS.textSecondary)};
  box-shadow: ${(p) => (p.$active ? `0 1px 3px ${COLORS.brandBlack}26` : 'none')};
  transition: background 0.15s ease;

  &:focus-visible {
    outline: 2px solid ${COLORS.brandGreen};
    outline-offset: 1px;
  }
`;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
  min-height: 64px;
`;

const ResultHint = styled.p`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  margin: 0;
  padding: ${SPACING.md};
  background: ${COLORS.gray100};
  border-radius: 10px;

  a {
    color: ${COLORS.brandGreenAccessible};
    font-weight: 700;
  }
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
`;

const ResultThumb = styled.div`
  flex: 0 0 56px;
  height: 56px;
  border-radius: 8px;
  background: ${COLORS.gray100};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ResultBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultPart = styled.div`
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.1;
`;

const ResultMeta = styled.div`
  ${TYPOGRAPHY.bodyXS}
  color: ${COLORS.textSecondary};
  overflow-wrap: anywhere;
`;

const ResultLink = styled.a`
  ${TYPOGRAPHY.buttonSM}
  flex-shrink: 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${COLORS.brandGreenAccessible};
  color: ${COLORS.white};
  text-decoration: none;

  &:hover {
    background: ${COLORS.brandGreenDark};
  }
`;

const Section = styled.section<{ $muted?: boolean }>`
  background: ${(p) => (p.$muted ? COLORS.gray100 : COLORS.white)};
  padding: ${SPACING.sectionLarge} ${SPACING.lg};
  scroll-margin-top: 130px;

  ${media.mobile} {
    padding: ${SPACING.sectionSmall} ${SPACING.md};
  }
`;

const SectionHead = styled.div`
  max-width: 1200px;
  margin: 0 auto ${SPACING.xl};
`;

const SectionTitle = styled.h2`
  font-family: ${DISPLAY_FONT};
  font-weight: 800;
  font-size: clamp(2rem, 4vw, 2.75rem);
  line-height: 1.05;
  text-transform: uppercase;
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.xs};
`;

const SectionText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin: 0;
`;

const CardGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${SPACING.lg};

  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: ${SPACING.md};
  }
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  overflow: hidden;
  scroll-margin-top: 140px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: ${COLORS.borderGray};
    box-shadow: 0 14px 36px ${COLORS.brandBlack}14;
  }

  &:target {
    border-color: ${COLORS.brandGreen};
    box-shadow: 0 0 0 3px ${COLORS.brandGreen}40;
  }
`;

// Product photos are shot on white, so the frame is white too - no grey box around a white photo.
const CardImage = styled(Link)`
  position: relative;
  display: block;
  height: 220px;
  margin: ${SPACING.md} ${SPACING.md} 0;
  background: ${COLORS.white};

  img {
    object-fit: contain;
    padding: ${SPACING.xs};
    transition: transform 0.25s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }

  ${media.mobile} {
    height: 200px;
  }
`;

const NoImage = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${DISPLAY_FONT};
  font-size: 2.5rem;
  color: ${COLORS.textMuted};
`;

const CardBody = styled.div`
  flex: 1;
  padding: ${SPACING.md} ${SPACING.lg} 0;
  border-top: 1px solid ${COLORS.borderLight};
  margin-top: ${SPACING.md};
`;

const CardKicker = styled.p`
  ${TYPOGRAPHY.label}
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${COLORS.brandGreenAccessible};
  margin: 0;
`;

const CardTitle = styled.h3`
  font-family: ${DISPLAY_FONT};
  font-weight: 800;
  font-size: 2.25rem;
  line-height: 1;
  margin: 2px 0 ${SPACING.md};

  a {
    color: ${COLORS.textPrimary};
    text-decoration: none;
  }

  a:hover {
    color: ${COLORS.brandGreenAccessible};
  }
`;

const CardSubtitle = styled.p`
  ${TYPOGRAPHY.bodySM}
  font-weight: 700;
  color: ${COLORS.textPrimary};
  margin: -${SPACING.xs} 0 ${SPACING.md};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.a`
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 1rem;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${COLORS.brandGreen}1f;
  color: ${COLORS.brandGreenAccessible};
  text-decoration: none;

  &:hover {
    background: ${COLORS.brandGreen}33;
  }
`;

const CareHint = styled.a`
  ${TYPOGRAPHY.bodySM}
  font-weight: 700;
  color: ${COLORS.brandGreenAccessible};
  text-decoration: none;
  padding: 4px 2px 0;

  &:hover {
    text-decoration: underline;
  }
`;

const FitHeading = styled.p`
  ${TYPOGRAPHY.label}
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${COLORS.textMuted};
  margin: 0 0 6px;
`;

const FitList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FitItem = styled.li`
  padding: 10px 12px;
  background: ${COLORS.gray200};
  border-radius: 10px;
`;

const FitRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm};
`;

const FitModel = styled.span`
  ${TYPOGRAPHY.bodySM}
  font-weight: 700;
  color: ${COLORS.textPrimary};
`;

const FitYears = styled.span`
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 1.1rem;
  color: ${COLORS.brandGreenAccessible};
  text-align: right;
`;

const FitEngines = styled.p`
  ${TYPOGRAPHY.bodyXS}
  color: ${COLORS.textSecondary};
  margin: 2px 0 0;
`;

// Price and "Add to cart" come from Shopify's buy widget (price above a full-width button);
// "View details" sits under it as a matching outlined button, so every card ends with the
// same three-line block at the same position.
const CardFooter = styled.div`
  margin-top: auto;
  padding: ${SPACING.md} ${SPACING.lg} ${SPACING.lg};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`;

const Price = styled.div`
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 1.75rem;
  color: ${COLORS.textPrimary};
  margin-bottom: 4px;
`;

const DetailsButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border: 2px solid ${COLORS.gray900};
  border-radius: 4px;
  font-family: ${FONT_FAMILY.primary};
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${COLORS.gray900};
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${COLORS.gray900};
    color: ${COLORS.white};
  }

  &:focus-visible {
    outline: 3px solid ${COLORS.brandGreen}66;
    outline-offset: 2px;
  }
`;

const Tabs = styled.div`
  max-width: 1200px;
  margin: 0 auto ${SPACING.md};
  display: flex;
  gap: ${SPACING.xs};
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  ${TYPOGRAPHY.buttonSM}
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs};
  min-height: 42px;
  padding: 0 ${SPACING.md};
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? COLORS.gray900 : COLORS.borderGray)};
  background: ${(p) => (p.$active ? COLORS.gray900 : COLORS.white)};
  color: ${(p) => (p.$active ? COLORS.white : COLORS.textPrimary)};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${COLORS.brandGreen};
    outline-offset: 2px;
  }
`;

const TabCount = styled.span`
  font-size: 12px;
  opacity: 0.7;
`;

const TableWrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 14px;
  overflow: hidden;

  ${media.mobile} {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${TYPOGRAPHY.bodySM}

  th {
    text-align: left;
    padding: 14px ${SPACING.md};
    background: ${COLORS.gray900};
    color: ${COLORS.white};
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    position: sticky;
    top: 0;
  }

  td {
    padding: 12px ${SPACING.md};
    border-top: 1px solid ${COLORS.borderLight};
    color: ${COLORS.textPrimary};
  }

  tbody tr:nth-child(even) td {
    background: ${COLORS.gray200};
  }

  tbody tr:hover td {
    background: ${COLORS.brandGreen}14;
  }
`;

const PartLink = styled.a<{ $kit?: boolean }>`
  display: inline-block;
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.02em;
  padding: 2px 10px;
  border-radius: 999px;
  text-decoration: none;
  background: ${(p) => (p.$kit ? COLORS.gray300 : `${COLORS.brandGreen}1f`)};
  color: ${(p) => (p.$kit ? COLORS.textPrimary : COLORS.brandGreenAccessible)};

  &:hover {
    background: ${(p) => (p.$kit ? COLORS.gray400 : `${COLORS.brandGreen}33`)};
  }
`;

const RowCards = styled.div`
  display: none;

  ${media.mobile} {
    display: flex;
    flex-direction: column;
    gap: ${SPACING.xs};
  }
`;

const RowCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  padding: ${SPACING.sm} ${SPACING.md};
`;

const RowCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm};
  ${TYPOGRAPHY.body}
`;

const RowCardMeta = styled.div`
  ${TYPOGRAPHY.bodySM}
  color: ${COLORS.textSecondary};
  margin-top: 2px;
`;

const Benefits = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${SPACING.lg};

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

const Benefit = styled.div`
  padding: ${SPACING.lg};
  background: ${COLORS.white};
  border-radius: 14px;
  border-top: 4px solid ${COLORS.brandGreen};
`;

const BenefitTitle = styled.h3`
  font-family: ${DISPLAY_FONT};
  font-weight: 700;
  font-size: 1.5rem;
  text-transform: uppercase;
  color: ${COLORS.textPrimary};
  margin: 0 0 ${SPACING.xs};
`;

const BenefitText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.textSecondary};
  margin: 0;

  a {
    color: ${COLORS.brandGreenAccessible};
    font-weight: 700;
  }
`;

const HelpBand = styled.section`
  background: ${COLORS.gray900};
  color: ${COLORS.white};
  text-align: center;
  padding: ${SPACING.sectionLarge} ${SPACING.lg};

  ${media.mobile} {
    padding: ${SPACING.sectionSmall} ${SPACING.md};
  }
`;

const HelpTitle = styled.h2`
  font-family: ${DISPLAY_FONT};
  font-weight: 800;
  font-size: clamp(2rem, 4vw, 2.75rem);
  text-transform: uppercase;
  color: ${COLORS.white};
  margin: 0 0 ${SPACING.xs};
`;

const HelpText = styled.p`
  ${TYPOGRAPHY.body}
  color: ${COLORS.gray400};
  margin: 0 0 ${SPACING.lg};
`;
